import type { CSSProperties } from 'react';

import classNames from 'classnames';

import { useAnchorable } from '../../hooks/useAnchorable';

import { Thumbnail } from './components/Thumbnail';

import { appendProtocol } from '../../utils/link';
import { buildRssThumbnailCaptionHtml } from './utils/buildRssThumbnailCaptionHtml';

import {
  articleContent,
  articleWrapper,
  byline,
  categoriesContainer,
  category,
  cta,
  ctaContainer,
  ctaLink as ctaLinkStyle,
  divider,
  dividerContainer,
  heading,
  headlineList,
  headlineListHeading,
  paragraph,
  pubDate,
  rssBody,
  rssContainer,
  rssTextBox,
} from './Rss.css';
import type { TPostRssElement, TPostRssProps } from './Rss.types';

type RssArticle = NonNullable<NonNullable<TPostRssElement['attrs']['data']>['content']>[number];
type RssSettings = NonNullable<TPostRssElement['attrs']['settings']>;

// Cap on time-window articles, guarding against clipped emails. Mirrors the client
// `RSS_TIME_WINDOW_MAX_ARTICLES` and backend `RssMethods::TIME_BASED_MAX_ARTICLES`.
const TIME_WINDOW_MAX_ARTICLES = 25;

// The "now" the time window is measured against. The backend stamps `data.window_anchor_at` at send
// and freezes it with the article list, so the published site filters against the same boundary as
// the email instead of a view-time cutoff that sheds articles as wall-clock time advances. Live/unsent
// content has no anchor → fall back to the current time.
const resolveReferenceTime = (anchorAt?: string): number => {
  if (!anchorAt) return Date.now();
  const timestamp = new Date(anchorAt).getTime();
  return Number.isNaN(timestamp) ? Date.now() : timestamp;
};

const parseRssPubDate = (pubDate?: string): number | null => {
  if (!pubDate) return null;

  // TZ-less feed dates are treated as UTC to match the email/editor render paths
  // (RssMethods#parse_article_pub_date, RSS/utils.ts). Without the `Z`, `new Date(...)` would read
  // the string in the render server's local zone and the published site would disagree with the email.
  const isoLike = pubDate.includes('T') ? pubDate : pubDate.replace(' ', 'T');
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(isoLike);
  const timestamp = new Date(hasZone ? isoLike : `${isoLike}Z`).getTime();

  return Number.isNaN(timestamp) ? null : timestamp;
};

/**
 * Resolve which articles render for this block. Mirrors the backend `RssMethods#articles_to_render`:
 * time-based selection keeps everything published within `timeWindowHours`, otherwise a fixed count.
 */
const selectRssArticles = (articles: RssArticle[], settings: RssSettings, anchorAt?: string): RssArticle[] => {
  // Mirror the backend `time_based_selection?` guard: time mode only applies with a positive
  // window. A non-positive/absent window falls through to count mode so all three render paths
  // (email, editor preview, published site) agree.
  const hours = settings.timeWindowHours ?? 0;
  if (settings.selectionMode === 'time' && hours > 0) {
    const cutoff = resolveReferenceTime(anchorAt) - hours * 60 * 60 * 1000;
    return articles
      .filter((article) => {
        const publishedAt = parseRssPubDate(article.pub_date);
        return publishedAt !== null && publishedAt >= cutoff;
      })
      .slice(0, TIME_WINDOW_MAX_ARTICLES);
  }

  const offset = settings.offset || 0;
  return articles.slice(offset, offset + (settings.entriesToShow || 1));
};

const rssThumbnailPositions = {
  LEFT: 'Left of content',
  RIGHT: 'Right of content',
  TOP: 'Above Title',
  BOTTOM: 'Below Title',
  ALTERNATING_HORIZONTAL: 'Alternating Horizontally',
};

type RssLinksSettings = {
  linkImage: boolean;
  linkTitle: boolean;
  displayCta: boolean;
  ctaType: 'Button' | 'Text';
  ctaText: string;
  underlineTitleLink: boolean;
};

const TITLE_LINK_STYLES = new Set([
  'Title as link',
  'Title and Thumbnail as link',
  'Title, Thumbnail and Button as CTA',
  'Title, Thumbnail and Link as CTA',
]);

const IMAGE_LINK_STYLES = new Set([
  'Thumbnail as link',
  'Title and Thumbnail as link',
  'Title, Thumbnail and Button as CTA',
  'Title, Thumbnail and Link as CTA',
]);

const VISIBLE_CTA_STYLES = new Set([
  'CTA button',
  'CTA link',
  'Title, Thumbnail and Button as CTA',
  'Title, Thumbnail and Link as CTA',
]);

const LINK_CTA_STYLES = new Set(['CTA link', 'Title, Thumbnail and Link as CTA']);

function getEffectiveRssLinksSettings(settings: TPostRssElement['attrs']['settings'] | undefined): RssLinksSettings {
  const ctaStyle = settings?.ctaStyle ?? 'CTA button';
  const legacySettings: RssLinksSettings = {
    linkImage: IMAGE_LINK_STYLES.has(ctaStyle),
    linkTitle: TITLE_LINK_STYLES.has(ctaStyle),
    displayCta: Boolean(settings?.displayCta) && VISIBLE_CTA_STYLES.has(ctaStyle),
    ctaType: LINK_CTA_STYLES.has(ctaStyle) ? 'Text' : 'Button',
    ctaText: settings?.ctaText || '',
    underlineTitleLink: false,
  };

  const hasExplicitLinksSettings =
    typeof settings?.linkImage === 'boolean' ||
    typeof settings?.linkTitle === 'boolean' ||
    settings?.ctaType !== undefined ||
    typeof settings?.underlineTitleLink === 'boolean';

  if (!hasExplicitLinksSettings) {
    return legacySettings;
  }

  return {
    linkImage: settings?.linkImage ?? legacySettings.linkImage,
    linkTitle: settings?.linkTitle ?? legacySettings.linkTitle,
    displayCta: settings?.displayCta ?? legacySettings.displayCta,
    ctaType: settings?.ctaType ?? legacySettings.ctaType,
    ctaText: settings?.ctaText || legacySettings.ctaText,
    underlineTitleLink: settings?.underlineTitleLink ?? legacySettings.underlineTitleLink,
  };
}

function rssCtaColorOverride(color: string | undefined): string | undefined {
  const nextColor = color?.trim();
  return nextColor || undefined;
}

function rssCtaButtonBackgroundOverride(settings: RssSettings | undefined): string | undefined {
  return rssCtaColorOverride(settings?.ctaButtonBackgroundColor);
}

function rssCtaButtonTextOverride(settings: RssSettings | undefined): string | undefined {
  return rssCtaColorOverride(settings?.ctaButtonTextColor);
}

function rssCtaButtonBorderOverride(settings: RssSettings | undefined): string | undefined {
  return rssCtaColorOverride(settings?.ctaButtonBorderColor);
}

function rssCtaLinkTextOverride(settings: RssSettings | undefined): string | undefined {
  return rssCtaColorOverride(settings?.ctaLinkTextColor);
}

function rssCtaLinkOverrideStyle(color: string | undefined): CSSProperties | undefined {
  if (!color) return undefined;

  return { color, borderBottomColor: color };
}

function getNumberOfColumns(entryLayout: string | null | undefined): number {
  if (!entryLayout) return 1;

  const columnCount = Number.parseInt(entryLayout.replace('-col', ''), 10);
  return Number.isNaN(columnCount) ? 1 : columnCount;
}

const getThumbnailSrc = (article: { thumbnail?: string }) => article.thumbnail ?? '';

const HEADLINES_ENTRY_LAYOUT = 'bullet-list';

const thumbnailIsHorizontal = (thumbnailPosition: string) => {
  return [
    rssThumbnailPositions.LEFT,
    rssThumbnailPositions.RIGHT,
    rssThumbnailPositions.ALTERNATING_HORIZONTAL,
  ].includes(thumbnailPosition);
};

function optionalPxFontSizeStyle(px: unknown): CSSProperties | undefined {
  if (typeof px !== 'number' || !Number.isFinite(px) || px <= 0) return undefined;
  return { fontSize: `${Math.round(px)}px` };
}

function titleHeadingLevel(columns: number, isHorizontal: boolean): string {
  if (isHorizontal) return 'h3';
  switch (columns) {
    case 1:
      return 'h2';
    case 2:
      return 'h3';
    case 3:
      return 'h4';
    default:
      return 'h5';
  }
}

function scaledFontSizeReduction(columns: number, isHorizontal: boolean, profile: 'default' | 'date_author'): number {
  const layoutColumns = isHorizontal ? 2 : columns;
  let reduction: number;
  if (profile === 'date_author') {
    switch (layoutColumns) {
      case 1:
        reduction = 1;
        break;
      case 2:
        reduction = 2;
        break;
      case 3:
        reduction = 3;
        break;
      case 4:
        reduction = 4;
        break;
      default:
        reduction = 1;
    }
  } else {
    switch (layoutColumns) {
      case 1:
        reduction = 0;
        break;
      case 2:
        reduction = 1;
        break;
      case 3:
        reduction = 2;
        break;
      case 4:
        reduction = 3;
        break;
      default:
        reduction = 1;
    }
  }
  return reduction;
}

function scaledFontSizeStyle(
  columns: number,
  isHorizontal: boolean,
  profile: 'default' | 'date_author',
  fontSizeVar = '--wt-paragraph-font-size',
): CSSProperties {
  const reduction = scaledFontSizeReduction(columns, isHorizontal, profile);
  if (reduction === 0) return {};
  return { fontSize: `calc(var(${fontSizeVar}, var(--wt-font-size)) - ${reduction}px)` };
}

function ctaLinkFontSizeStyle(columns: number, isHorizontal: boolean): CSSProperties {
  const baseFontSize = cssCalcDelta(
    '--wt-rss-cta-link-font-size',
    -scaledFontSizeReduction(columns, isHorizontal, 'default'),
    'var(--wt-paragraph-font-size, var(--wt-font-size))',
  );
  return { fontSize: baseFontSize };
}

function rssCtaButtonSize(settings: RssSettings | undefined): 'small' | 'normal' | 'large' | 'full' | undefined {
  if (!settings) return undefined;

  const camel = settings.ctaButtonSize;
  if (camel === 'small' || camel === 'normal' || camel === 'large' || camel === 'full') return camel;
  const snake = Reflect.get(settings, 'cta_button_size');
  if (snake === 'small' || snake === 'normal' || snake === 'large' || snake === 'full') return snake;
  return undefined;
}

function cssCalcDelta(variableName: string, delta: number, fallback: string): string {
  if (delta === 0) return `var(${variableName}, ${fallback})`;
  if (delta < 0) return `calc(var(${variableName}, ${fallback}) - ${Math.abs(delta)}px)`;
  return `calc(var(${variableName}, ${fallback}) + ${delta}px)`;
}

function rssCtaColumnSizeDeltas(
  columns: number,
  isHorizontal: boolean,
): {
  font: number;
  topBottom: number;
  leftRight: number;
} {
  const layoutColumns = isHorizontal ? 2 : columns;
  if (layoutColumns === 1) return { font: 0, topBottom: 0, leftRight: 0 };
  if (layoutColumns === 2) return { font: -1, topBottom: -1, leftRight: -2 };
  if (layoutColumns === 3) return { font: -2, topBottom: -2, leftRight: -4 };
  if (layoutColumns === 4) return { font: -3, topBottom: -3, leftRight: -6 };
  return { font: -1, topBottom: -1, leftRight: -2 };
}

function rssCtaButtonSizingStyle(
  settings: RssSettings | undefined,
  columns: number,
  isHorizontal: boolean,
): CSSProperties {
  const size = rssCtaButtonSize(settings);
  const columnDeltas = rssCtaColumnSizeDeltas(columns, isHorizontal);
  let fontDelta = columnDeltas.font;
  let padTopBottom = columnDeltas.topBottom;
  let padLeftRight = columnDeltas.leftRight;
  if (size === 'small') {
    fontDelta += -2;
    padTopBottom += -2;
    padLeftRight += -3;
  } else if (size === 'large') {
    fontDelta += 2;
    padTopBottom += 4;
    padLeftRight += 8;
  }

  const fontSize = cssCalcDelta(
    '--wt-rss-cta-button-font-size',
    fontDelta,
    'var(--wt-button-font-size, var(--wt-font-size))',
  );
  const style: CSSProperties = {
    fontSize,
    lineHeight: `calc(${fontSize} + 2px)`,
    padding: `${cssCalcDelta('--wt-rss-cta-button-padding-top', padTopBottom, 'var(--wt-button-padding-top, 0px)')} ${cssCalcDelta(
      '--wt-rss-cta-button-padding-right',
      padLeftRight,
      'var(--wt-button-padding-right, 14px)',
    )} ${cssCalcDelta('--wt-rss-cta-button-padding-bottom', padTopBottom, 'var(--wt-button-padding-bottom, 0px)')} ${cssCalcDelta(
      '--wt-rss-cta-button-padding-left',
      padLeftRight,
      'var(--wt-button-padding-left, 14px)',
    )}`,
  };
  if (size === 'full') {
    style.width = '100%';
    style.boxSizing = 'border-box';
  }
  return style;
}

function rssCtaButtonAnchorStyle(settings: RssSettings | undefined): CSSProperties | undefined {
  if (rssCtaButtonSize(settings) !== 'full') return undefined;
  return { display: 'block', width: '100%' };
}

function rssSettingsFontSize(
  settings: TPostRssElement['attrs']['settings'] | undefined,
  camelKey: 'dateAuthorFontSize' | 'descriptionFontSize' | 'ctaLinkFontSize',
  snakeKey: string,
): CSSProperties | undefined {
  if (!settings) return undefined;
  const raw = (settings as Record<string, unknown>)[camelKey] ?? (settings as Record<string, unknown>)[snakeKey];
  return optionalPxFontSizeStyle(raw);
}

type RssTextBoxPaddingSides = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

function rssTextBoxPaddingSides(settings: TPostRssElement['attrs']['settings'] | undefined): RssTextBoxPaddingSides {
  if (!settings) return { top: 0, right: 0, bottom: 0, left: 0 };
  const rawSettings = settings as Record<string, unknown>;
  const side = (value: unknown) => {
    const n = typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : 0;
    return n > 0 ? n : 0;
  };
  const useIndividual =
    settings.textBoxUseIndividualPadding === true || rawSettings.text_box_use_individual_padding === true;
  if (useIndividual) {
    return {
      top: side(settings.textBoxPaddingTop ?? rawSettings.text_box_padding_top),
      right: side(settings.textBoxPaddingRight ?? rawSettings.text_box_padding_right),
      bottom: side(settings.textBoxPaddingBottom ?? rawSettings.text_box_padding_bottom),
      left: side(settings.textBoxPaddingLeft ?? rawSettings.text_box_padding_left),
    };
  }
  const u = side(settings.textBoxPadding ?? rawSettings.text_box_padding);
  return { top: u, right: u, bottom: u, left: u };
}

/** Matches email `rss_text_box` / Swarm Write preview (`RssView` `rssTextBoxPaddingStyle`). */
function rssTextBoxPaddingStyle(settings: TPostRssElement['attrs']['settings'] | undefined): CSSProperties | undefined {
  const { top, right, bottom, left } = rssTextBoxPaddingSides(settings);
  if (top === 0 && right === 0 && bottom === 0 && left === 0) {
    return undefined;
  }
  return {
    boxSizing: 'border-box',
    paddingTop: top,
    paddingRight: right,
    paddingBottom: bottom,
    paddingLeft: left,
  };
}

function rssTitleAboveThumbnailSpacingStyle(
  settings: TPostRssElement['attrs']['settings'] | undefined,
): CSSProperties | undefined {
  const { top, right, left } = rssTextBoxPaddingSides(settings);
  if (top === 0 && right === 0 && left === 0) return undefined;
  return {
    boxSizing: 'border-box',
    ...(top > 0 ? { paddingBottom: top } : {}),
    ...(right > 0 ? { paddingRight: right } : {}),
    ...(left > 0 ? { paddingLeft: left } : {}),
  };
}

export const PostRss = (props: TPostRssProps) => {
  const { element, rewriteImageSrc } = props;
  const { attrs } = element;

  const { anchorAttributes } = useAnchorable(attrs);

  const { data, settings } = attrs;
  const entryLayout = attrs.entryLayout ?? '1-col';
  const effectiveNumberOfColumns = thumbnailIsHorizontal(settings?.thumbnailPosition || '')
    ? 1
    : getNumberOfColumns(entryLayout);
  const sanitizedImageWidthPercentage =
    typeof settings?.imageWidthPercentage === 'number' && Number.isFinite(settings.imageWidthPercentage)
      ? Math.max(20, Math.min(60, Math.round(settings.imageWidthPercentage)))
      : 50;

  const linksSettings = getEffectiveRssLinksSettings(settings);
  const ctaButtonBackgroundOverride = rssCtaButtonBackgroundOverride(settings);
  const ctaButtonTextOverride = rssCtaButtonTextOverride(settings);
  const ctaButtonBorderOverride = rssCtaButtonBorderOverride(settings);
  const ctaLinkTextOverride = rssCtaLinkTextOverride(settings);

  const showTitleAboveThumbnail = settings?.thumbnailPosition === rssThumbnailPositions.BOTTOM;

  const ctaButton = linksSettings.displayCta && linksSettings.ctaType === 'Button';
  const ctaLink = linksSettings.displayCta && linksSettings.ctaType === 'Text';
  const titleLinkStyle: CSSProperties = {
    color: 'inherit',
    textDecoration: linksSettings.underlineTitleLink ? 'underline' : 'none',
  };

  const articleWrapperClassNames = (index: number): string => {
    if (settings?.thumbnailPosition === rssThumbnailPositions.ALTERNATING_HORIZONTAL) {
      if (settings?.rowLayout !== 'alternating-left') {
        return index % 2 === 0 ? 'flex-col sm:flex-row-reverse' : 'flex-col sm:flex-row';
      }
      return index % 2 === 0 ? 'flex-col sm:flex-row' : 'flex-col sm:flex-row-reverse';
    }
    if (settings?.thumbnailPosition === rssThumbnailPositions.LEFT) {
      return 'flex-col sm:flex-row';
    }
    if (settings?.thumbnailPosition === rssThumbnailPositions.RIGHT) {
      return 'flex-col sm:flex-row-reverse';
    }

    return 'flex-col';
  };

  const horizontalImageSpacingClassNames = (index: number): string => {
    if (settings?.thumbnailPosition === rssThumbnailPositions.ALTERNATING_HORIZONTAL) {
      if (settings?.rowLayout !== 'alternating-left') {
        return index % 2 === 0 ? 'sm:pl-[--wt-hyperlink-padding]' : 'sm:pr-[--wt-hyperlink-padding]';
      }
      return index % 2 === 0 ? 'sm:pr-[--wt-hyperlink-padding]' : 'sm:pl-[--wt-hyperlink-padding]';
    }
    if (settings?.thumbnailPosition === rssThumbnailPositions.LEFT) {
      return 'sm:pr-[--wt-hyperlink-padding]';
    }
    if (settings?.thumbnailPosition === rssThumbnailPositions.RIGHT) {
      return 'sm:pl-[--wt-hyperlink-padding]';
    }

    return '';
  };

  const imageVerticalAlignmentClassName = (): string => {
    if (!thumbnailIsHorizontal(settings?.thumbnailPosition || '')) return '';

    if (settings?.imageVerticalAlign === 'top') return 'sm:self-start';
    if (settings?.imageVerticalAlign === 'bottom') return 'sm:self-end';

    return 'sm:self-center';
  };

  const textVerticalAlignmentClassName = (): string => {
    if (!thumbnailIsHorizontal(settings?.thumbnailPosition || '')) return '';

    if (settings?.textVerticalAlign === 'top') return 'sm:self-start';
    if (settings?.textVerticalAlign === 'bottom') return 'sm:self-end';

    return 'sm:self-center';
  };

  const textAlignCss =
    settings?.textAlign === 'center' || settings?.textAlign === 'right' ? settings.textAlign : 'left';
  const categoriesJustifyCss =
    textAlignCss === 'center' ? 'center' : textAlignCss === 'right' ? 'flex-end' : 'flex-start';

  const ctaAlignCss = (() => {
    const c = settings?.ctaAlignment?.toLowerCase();
    if (c === 'center' || c === 'right') return c;
    return 'left';
  })();

  const isHorizontal = thumbnailIsHorizontal(settings?.thumbnailPosition || '');
  const titleFontSizeStyle: CSSProperties | undefined = optionalPxFontSizeStyle(settings?.titleFontSize) ?? {
    fontSize: `var(--wt-${titleHeadingLevel(effectiveNumberOfColumns, isHorizontal)}-font-size)`,
  };
  const dateAuthorFontSizeStyle =
    rssSettingsFontSize(settings, 'dateAuthorFontSize', 'date_author_font_size') ??
    scaledFontSizeStyle(effectiveNumberOfColumns, isHorizontal, 'date_author');
  const descriptionFontSizeStyle =
    rssSettingsFontSize(settings, 'descriptionFontSize', 'description_font_size') ??
    scaledFontSizeStyle(effectiveNumberOfColumns, isHorizontal, 'default');
  const ctaLinkFontSizeOverride = rssSettingsFontSize(settings, 'ctaLinkFontSize', 'cta_link_font_size');
  const ctaLinkFontSize = ctaLinkFontSizeOverride ?? ctaLinkFontSizeStyle(effectiveNumberOfColumns, isHorizontal);
  const ctaButtonSizingStyle = rssCtaButtonSizingStyle(settings, effectiveNumberOfColumns, isHorizontal);
  const ctaButtonAnchorStyle = rssCtaButtonAnchorStyle(settings);
  const captionFontSizeStyle = scaledFontSizeStyle(
    effectiveNumberOfColumns,
    isHorizontal,
    'default',
    '--wt-image-source-font-size',
  );
  const textBoxPaddingStyle = rssTextBoxPaddingStyle(settings);
  const visibleArticles = selectRssArticles(data?.content ?? [], settings ?? {}, data?.window_anchor_at);

  if (entryLayout === HEADLINES_ENTRY_LAYOUT) {
    return (
      <div
        {...anchorAttributes}
        className={rssContainer}
        style={
          {
            '--number-of-columns': 1,
            '--rss-text-align': 'left',
          } as CSSProperties
        }
      >
        <div className={rssTextBox} style={textBoxPaddingStyle}>
          <ul className={headlineList}>
            {visibleArticles.map((article, index) => {
              const articleLink = article.link ? appendProtocol(article.link) : article.link;
              const titleAsLink = linksSettings.linkTitle && Boolean(article.link);

              return (
                <li key={article.link || article.title || index}>
                  {titleAsLink ? (
                    <a
                      href={articleLink}
                      target="_blank"
                      rel="noreferrer"
                      data-skip-tracking="true"
                      style={titleLinkStyle}
                    >
                      <h2
                        data-rss-title
                        className={classNames(heading, headlineListHeading)}
                        style={titleFontSizeStyle}
                      >
                        {article.title}
                      </h2>
                    </a>
                  ) : (
                    <h2 data-rss-title className={classNames(heading, headlineListHeading)} style={titleFontSizeStyle}>
                      {article.title}
                    </h2>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div
      {...anchorAttributes}
      className={rssContainer}
      style={
        {
          '--number-of-columns': effectiveNumberOfColumns,
          '--cta-alignment': ctaAlignCss,
          '--rss-image-width': `${sanitizedImageWidthPercentage}%`,
          '--rss-text-align': textAlignCss,
          '--rss-categories-justify': categoriesJustifyCss,
        } as CSSProperties
      }
    >
      {visibleArticles.map((article, index) => {
        const thumbnailSrc = getThumbnailSrc(article);
        const articleThumbnailSrc = thumbnailSrc && rewriteImageSrc ? rewriteImageSrc(thumbnailSrc) : thumbnailSrc;
        const articleLink = article.link ? appendProtocol(article.link) : article.link;
        const thumbnailAsLink = linksSettings.linkImage && Boolean(article.link);
        const titleAsLink = linksSettings.linkTitle && Boolean(article.link);
        const thumbnailCaptionHtml = buildRssThumbnailCaptionHtml(article, settings);
        const showColumnThumbnail =
          Boolean(settings?.displayThumbnail && articleThumbnailSrc) &&
          !thumbnailIsHorizontal(settings?.thumbnailPosition || '');
        const titleAboveSpacingStyle = showColumnThumbnail ? rssTitleAboveThumbnailSpacingStyle(settings) : undefined;
        const showBody = Boolean(
          (settings?.displayAuthor && article.author) ||
            (settings?.displayPubDate && article.pub_date) ||
            (settings?.displayDescription && article.description) ||
            (settings?.displayContent && article.content),
        );

        return (
          <div key={article.link}>
            {settings?.showDividers &&
              index > 0 &&
              (thumbnailIsHorizontal(settings?.thumbnailPosition || '') || entryLayout === '1-col') && (
                <div className={dividerContainer}>
                  <div className={divider} />
                </div>
              )}
            <div
              className={classNames(
                articleWrapper,
                articleWrapperClassNames(index),
                thumbnailIsHorizontal(settings?.thumbnailPosition || '') ? 'sm:items-start' : '',
              )}
            >
              {settings?.displayThumbnail &&
              articleThumbnailSrc &&
              thumbnailIsHorizontal(settings?.thumbnailPosition || '') ? (
                <div
                  className={classNames(
                    'w-full sm:shrink-0 sm:basis-[var(--rss-image-width)] sm:w-[var(--rss-image-width)]',
                    imageVerticalAlignmentClassName(),
                    horizontalImageSpacingClassNames(index),
                  )}
                >
                  <Thumbnail
                    asLink={thumbnailAsLink}
                    articleLink={articleLink}
                    articleTitle={article.title}
                    articleThumbnailSrc={articleThumbnailSrc}
                    captionHtml={thumbnailCaptionHtml}
                    captionFontSizeStyle={captionFontSizeStyle}
                  />
                </div>
              ) : null}
              <div className={classNames(articleContent, textVerticalAlignmentClassName())}>
                {settings?.displayTitle &&
                showTitleAboveThumbnail &&
                !thumbnailIsHorizontal(settings?.thumbnailPosition || '') ? (
                  titleAsLink && article.link ? (
                    <a
                      href={articleLink}
                      target="_blank"
                      rel="noreferrer"
                      data-skip-tracking="true"
                      style={titleLinkStyle}
                    >
                      <h2
                        data-rss-title
                        className={heading}
                        style={{ ...titleFontSizeStyle, ...titleAboveSpacingStyle }}
                      >
                        {article.title}
                      </h2>
                    </a>
                  ) : (
                    <h2 data-rss-title className={heading} style={{ ...titleFontSizeStyle, ...titleAboveSpacingStyle }}>
                      {article.title}
                    </h2>
                  )
                ) : null}
                {settings?.displayThumbnail &&
                articleThumbnailSrc &&
                !thumbnailIsHorizontal(settings?.thumbnailPosition || '') ? (
                  <Thumbnail
                    asFullWidth
                    asLink={thumbnailAsLink}
                    articleLink={articleLink}
                    articleTitle={article.title}
                    articleThumbnailSrc={articleThumbnailSrc}
                    captionHtml={thumbnailCaptionHtml}
                    captionFontSizeStyle={captionFontSizeStyle}
                  />
                ) : null}
                <div className={rssTextBox} style={textBoxPaddingStyle}>
                  {thumbnailIsHorizontal(settings?.thumbnailPosition || '') &&
                    settings?.displayCategories &&
                    article.categories &&
                    article.categories.length > 0 && (
                      <div className={categoriesContainer}>
                        {article.categories.map((c) => (
                          <span key={c} className={category}>
                            {c}
                          </span>
                        ))}
                      </div>
                    )}

                  {!thumbnailIsHorizontal(settings?.thumbnailPosition || '') &&
                    settings?.displayCategories &&
                    article.categories &&
                    article.categories.length > 0 && (
                      <div className={categoriesContainer}>
                        {article.categories.map((c) => (
                          <span key={c} className={category}>
                            {c}
                          </span>
                        ))}
                      </div>
                    )}

                  {settings?.displayTitle &&
                  (thumbnailIsHorizontal(settings?.thumbnailPosition || '') || !showTitleAboveThumbnail) ? (
                    titleAsLink && article.link ? (
                      <a
                        href={articleLink}
                        target="_blank"
                        rel="noreferrer"
                        data-skip-tracking="true"
                        style={titleLinkStyle}
                      >
                        <h2 data-rss-title className={heading} style={titleFontSizeStyle}>
                          {article.title}
                        </h2>
                      </a>
                    ) : (
                      <h2 data-rss-title className={heading} style={titleFontSizeStyle}>
                        {article.title}
                      </h2>
                    )
                  ) : null}
                  {showBody && (
                    <div data-rss-content className={rssBody}>
                      {settings?.displayAuthor && article.author && settings?.displayPubDate && article.pub_date && (
                        <p data-rss-author className={byline} style={dateAuthorFontSizeStyle}>
                          by {article.author}
                          <br />
                          {new Date(article.pub_date).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </p>
                      )}
                      {settings?.displayAuthor && article.author && !(settings?.displayPubDate && article.pub_date) && (
                        <p data-rss-author className={byline} style={dateAuthorFontSizeStyle}>
                          by {article.author}
                        </p>
                      )}
                      {settings?.displayPubDate && article.pub_date && !(settings?.displayAuthor && article.author) && (
                        <p data-rss-date className={pubDate} style={dateAuthorFontSizeStyle}>
                          {new Date(article.pub_date).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </p>
                      )}
                      {settings?.displayDescription && article.description && (
                        <div
                          data-rss-description
                          className={paragraph}
                          style={descriptionFontSizeStyle}
                          dangerouslySetInnerHTML={{ __html: article.description }}
                        />
                      )}
                      {settings?.displayContent && article.content && (
                        <div
                          data-rss-full-article
                          className={paragraph}
                          style={descriptionFontSizeStyle}
                          dangerouslySetInnerHTML={{ __html: article.content }}
                        />
                      )}
                    </div>
                  )}
                  {(ctaButton || ctaLink) && article.link && (
                    <div className={ctaContainer}>
                      <a
                        href={articleLink}
                        target="_blank"
                        rel="noreferrer"
                        data-skip-tracking="true"
                        className={ctaLink ? ctaLinkStyle : undefined}
                        style={
                          ctaLink
                            ? { ...ctaLinkFontSize, ...rssCtaLinkOverrideStyle(ctaLinkTextOverride) }
                            : ctaButtonAnchorStyle
                        }
                        {...(ctaLink ? { 'data-rss-cta-link': '' } : {})}
                      >
                        {ctaButton ? (
                          <button
                            type="button"
                            data-rss-cta-button
                            className={cta}
                            style={{
                              ...ctaButtonSizingStyle,
                              ...(ctaButtonBackgroundOverride ? { backgroundColor: ctaButtonBackgroundOverride } : {}),
                              ...(ctaButtonTextOverride ? { color: ctaButtonTextOverride } : {}),
                              ...(ctaButtonBorderOverride ? { borderColor: ctaButtonBorderOverride } : {}),
                            }}
                          >
                            {linksSettings.ctaText || ''}
                          </button>
                        ) : (
                          linksSettings.ctaText || ''
                        )}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
